import React, { useState } from 'react';
import { Wifi, WifiOff, RefreshCw, Download, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useApiSync } from '../../hooks/useApiSync';

/**
 * API Sync Status Indicator
 * Shows connection status and provides sync/export actions
 * Only visible in dev mode
 */
export default function ApiSyncStatus() {
  const {
    isEnabled,
    isConnected,
    isSyncing,
    queuedCount,
    syncNow,
    triggerExport
  } = useApiSync();

  const [showActions, setShowActions] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  // Don't render if not in dev mode
  if (!isEnabled) {
    return null;
  }

  const handleSync = async () => {
    const result = await syncNow();
    if (result.success) {
      setExportStatus('synced');
      setTimeout(() => setExportStatus(null), 2000);
    } else {
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
    }
  };

  const handleExport = async () => {
    setExportStatus('exporting');
    const result = await triggerExport();

    if (result.success) {
      setExportStatus('exported');
      setTimeout(() => {
        setExportStatus(null);
        // Reload page to show updated data
        window.location.reload();
      }, 1500);
    } else {
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 3000);
    }
  };

  return (
    <div className="relative">
      {/* Status Indicator Button */}
      <button
        onClick={() => setShowActions(!showActions)}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
          transition-all duration-200
          ${isConnected
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
            : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
          }
        `}
        title={isConnected ? 'API Connected' : 'API Disconnected'}
      >
        {isConnected ? (
          <Wifi className="w-4 h-4" />
        ) : (
          <WifiOff className="w-4 h-4" />
        )}

        {queuedCount > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-white/50 dark:bg-black/20 text-xs font-bold">
            {queuedCount}
          </span>
        )}

        <span className="hidden sm:inline">
          {isConnected ? 'API' : 'Offline'}
        </span>
      </button>

      {/* Actions Dropdown */}
      {showActions && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowActions(false)}
          />

          {/* Dropdown */}
          <div className="
            absolute right-0 mt-2 w-56 z-50
            bg-white dark:bg-gray-800 rounded-xl shadow-lg
            border border-gray-200 dark:border-gray-700
            p-2
          ">
            {/* Connection Status */}
            <div className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 mb-2">
              <div className="flex items-center justify-between">
                <span>Connection Status:</span>
                <span className={`font-medium ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              {queuedCount > 0 && (
                <div className="flex items-center justify-between mt-1">
                  <span>Queued Changes:</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">{queuedCount}</span>
                </div>
              )}
            </div>

            {/* Sync Button */}
            <button
              onClick={handleSync}
              disabled={isSyncing || exportStatus === 'exporting'}
              className="
                w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                text-left transition-colors
                hover:bg-gray-100 dark:hover:bg-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {exportStatus === 'synced' ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-600 dark:text-green-400">Synced!</span>
                </>
              ) : isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Sync Now</span>
                  {queuedCount > 0 && (
                    <span className="ml-auto text-xs text-gray-500">({queuedCount})</span>
                  )}
                </>
              )}
            </button>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exportStatus === 'exporting' || isSyncing}
              className="
                w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                text-left transition-colors
                hover:bg-gray-100 dark:hover:bg-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {exportStatus === 'exported' ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-600 dark:text-green-400">Exported!</span>
                </>
              ) : exportStatus === 'exporting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Export & Reload</span>
                </>
              )}
            </button>

            {exportStatus === 'error' && (
              <div className="mt-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Operation failed. Check console for details.</span>
                </div>
              </div>
            )}

            <div className="mt-2 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
              Use "Export & Reload" to sync changes back to JSON files
            </div>
          </div>
        </>
      )}
    </div>
  );
}
